import {put, call} from 'redux-saga/effects';
import Court from "../../entities/court";
import * as Actions from "../actions/courtActions";
import * as idb from '../../lib/idbd';
import * as api from '../../rest/api';
import {processQueue} from "../../queues/processQueue";
import newHRTimestamp from "../../lib/picos/timestamp";
import {tabSession} from '../../Auth/session';

// Inlined to reduce circular dependencies
function getCurrentUserId() {
    return sessionStorage.getItem('userId');
}

// Inlined to reduce circular dependencies
function getCurrentFirmId() {
    return sessionStorage.getItem('firmId');
}

type CourtAction = {
    courtId: string;
    court: Court;
    courts: Court[];
    onQueued?: (saved: boolean) => void;
};

export function* fetchOneCourt(action: CourtAction) {
	const session = tabSession.captureGeneration();
	try {
		const court: Court = yield call(api.findOne, 'court', action.courtId);
		session.assertCurrent();

		// Now we can update the user interface
		yield put({type: Actions.COURT_FETCH_ONE_SUCCESS, courtId: action.courtId, court});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.COURT_FETCH_ONE_FAILURE, courtId: action.courtId, status: 500, err});

		console.error('Error fetching from database', err);
	}
}

/**
 * @param {CourtAction} action
 */
export function* saveCourt(action: CourtAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    // console.debug('⮂ COURT_SAVE saga', action.court?.id);
    
	if (action.court === undefined || action.court === null) {
		console.error('saveCourt received a null');
		throw new Error('saveCourt received a null');
	}

	if (!(action.court instanceof Court)) {
		action.court = new Court(action.court);
	}

	try {
		// Save to IndexedDB
		const addResult = yield call(idb.add, 'court', action.court);
		if (addResult.isErr()) {
			console.error(`⮂ saveCourt: IDB write failed: ${addResult.unwrapErr().message}`);
			reportQueued(false);
			session.assertCurrent();
			yield put({type: Actions.COURT_SAVE_FAILURE, court: action.court, status: 500, message: addResult.unwrapErr().message});
			return;
		}
		reportQueued(true);
		session.assertCurrent();

		// Now we can update the user interface
		yield put({type: Actions.COURT_SAVE, court: action.court});

		// A save never deletes autosaved drafts (OWA-958). The editor that wrote a draft
		// removes it when it saves; a draft typed somewhere else may be newer than this copy.
		session.assertCurrent();

		// Let the queue know to process
		yield call(processQueue);
	}
	catch(err) {
		if (session.signal.aborted) { reportQueued(false); return; }
		reportQueued(false);
		yield put({type: Actions.COURT_SAVE_FAILURE, court: action.court, status: 500, err});

		console.error('Error saving to database', err);
	}
}

/**
 * TODO: Update for saveMany
 * @param {CourtAction} action
 * @param {Court[]} action.courts
 */
export function* saveManyCourts(action: CourtAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    // console.debug('⮂ COURT_SAVE_MANY saga', action.courts.map(court => court.id).join(','));
    
    if (!Array.isArray(action.courts)) {
        console.error('saveManyCourts expected an array, received', action.courts);
        throw new Error('saveManyCourts expected an array');
    }

	try {
		const toSave: Court[] = [];

		// Save each item to IndexedDB
		for(let i = 0; i < action.courts.length; i++) {
			const court = action.courts[i];
			court.persisted = false;
			toSave.push(court);
		}

		const addResult = yield call(idb.add, 'court', toSave);
		if (addResult.isErr()) {
			console.error(`⮂ saveManyCourts: IDB write failed: ${addResult.unwrapErr().message}`);
			reportQueued(false);
			session.assertCurrent();
			yield put({type: Actions.COURT_SAVE_MANY_FAILURE, courts: action.courts, status: 500, message: addResult.unwrapErr().message});
			return;
		}
		reportQueued(true);
		session.assertCurrent();

		// Now we can update the user interface
		yield put({type: Actions.COURT_SAVE_MANY, courts: toSave});
		session.assertCurrent();

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		if (session.signal.aborted) { reportQueued(false); return; }
		reportQueued(false);
		yield put({type: Actions.COURT_SAVE_MANY_FAILURE, courts: action.courts, status: 500, err});

		console.error(`Error saving ${action.courts.length} courts to database`, err);
	}
}

/**
 * @param {CourtAction} action
 */
export function* removeCourt(action: CourtAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
	if (!userId || action.court === undefined || action.court === null || !(action.court instanceof Court)) {
			console.error('removeCourt expected type Court, received', action.court);
			throw new Error('removeCourt expected type Court');
		}

	try {
		// Mark as deleted
		action.court.status = 'D';
		action.court.addedOn = newHRTimestamp();
		action.court.addedBy = userId;

		// Save to IndexedDB
		const addResult = yield call(idb.add, 'court', action.court);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ removeCourt: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.COURT_REMOVE_FAILURE, court: action.court, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.COURT_REMOVE, court: action.court});
		session.assertCurrent();

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.COURT_REMOVE_FAILURE, court: action.court, status: 500, err});

		console.error('Error deleting from database', err);
	}
}

/**
	* Restore the last version of this Court.
	*
	* @param {CourtAction} action
	*/
export function* restoreCourt(action: CourtAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
    if (!userId) {
        console.error('restoreCourt expected getCurrentUserId() to return a value');
        return;
    }
    
	if (action.court === undefined || action.court === null) {
        console.error('restoreCourt expected a Court, received null');
        throw new Error('restoreCourt expected a Court, received null');
    }
    else if (!(action.court instanceof Court)) action.court = new Court(action.court); 
    
	try {
		// Mark as restored
		action.court.status = 'U';
		action.court.addedOn = newHRTimestamp();
		action.court.addedBy = userId;
		action.court.restore = true;

		// Save to IndexedDB
		const addResult = yield call(idb.add, 'court', action.court);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ restoreCourt: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.COURT_RESTORE_FAILURE, court: action.court, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.COURT_RESTORE, court: action.court});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.COURT_RESTORE_FAILURE, court: action.court, status: 500, err});

		console.error('Error restoring from database', err);
	}
}